import { createInstance } from '@optimizely/react-sdk';

/**
 * @typedef {Object.<string, string>} OptimizelyVariationDefinition
 * Treatment variations only - 'off' is implicit and serves as control
 */

/**
 * @typedef {Object} OptimizelyFlagDefinition
 * @property {string} key - The flag key used in Optimizely
 * @property {OptimizelyVariationDefinition} variations - Treatment variations
 */

/**
 * Optimizely feature flags with variation definitions.
 * These map to flags configured in the Optimizely dashboard.
 *
 * @type {Object.<string, OptimizelyFlagDefinition>}
 */
export const OPTIMIZELY_FLAGS = {
	/** Facility page V2 redesign experiment */
	FACILITY_PAGE_REDESIGN: {
		key: 'sfdc_facility_redesign_flag',
		variations: {
			REDESIGN: 'sfdc_facility_redesign_page_variation',
		},
	},
	/** Unit reservation form A/A test for reporting verification */
	UNIT_RESERVATION_AA_TEST: {
		key: 'sfdc_unit_reservation_aa_test',
		variations: {
			TREATMENT: 'treatment',
		},
	},
	FILTERS_TEST: {
		key: 'sfdc_filter_redesign_flag',
		variations: {
			TREATMENT: 'sfdc_new_filters_variation',
		},
	},
};

/**
 * Create Optimizely client for client-side use.
 * When datafile is provided (from SSR), client is immediately ready.
 * When only sdkKey is provided, client fetches datafile async.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.sdkKey - Optimizely SDK key for auto-updates
 * @param {Object} [options.datafile] - Pre-fetched datafile from server
 * @returns {ReactSDKClient} Optimizely client instance
 */
export const createOptimizelyClient = ({ sdkKey, datafile = null }) => {
	if (!sdkKey && !datafile) {
		console.warn('Optimizely: No sdkKey or datafile provided');
		return null;
	}

	const config = {
		sdkKey,
		datafileOptions: {
			autoUpdate: true,
			updateInterval: 300000, // 5 minutes
		},
	};

	// Add datafile if provided (enables synchronous initialization)
	if (datafile) {
		config.datafile = datafile;
	}

	return createInstance(config);
};

export default createOptimizelyClient;
